"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PanelLeft, PanelRight } from "lucide-react";
import { cn } from "@/lib/utils";

// Portal-based tooltip component for the collapse button
interface PortalTooltipProps {
  isVisible: boolean;
  targetRef: React.RefObject<HTMLButtonElement | null>;
  children: React.ReactNode;
}

function PortalTooltip({ isVisible, targetRef, children }: PortalTooltipProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (isVisible && targetRef.current && mounted) {
      const rect = targetRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8, // 8px margin from button
      });
    }
  }, [isVisible, targetRef, mounted]);

  if (!mounted || !isVisible) return null;

  return createPortal(
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0, x: -5 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "fixed",
          top: `${position.top}px`,
          left: `${position.left}px`,
          transform: "translateY(-50%)",
          zIndex: 9999,
        }}
        className="flex items-center gap-2 bg-foreground/95 text-background px-2.5 py-1.5 rounded-md text-xs whitespace-nowrap pointer-events-none"
      >
        {children}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

interface SidebarHeaderProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  isLogoHovered: boolean;
  setIsLogoHovered: (hovered: boolean) => void;
  isCollapseHovered: boolean;
  setIsCollapseHovered: (hovered: boolean) => void;
  onNewChat: () => void;
}

export function SidebarHeader({
  isCollapsed,
  setIsCollapsed,
  isLogoHovered,
  setIsLogoHovered,
  isCollapseHovered,
  setIsCollapseHovered,
  onNewChat,
}: SidebarHeaderProps) {
  const collapseButtonRef = useRef<HTMLButtonElement>(null);

  // Handle logo/name click - navigate to root or expand sidebar
  const handleLogoClick = () => {
    if (isCollapsed) {
      // If collapsed, expand the sidebar
      setIsLogoHovered(false);
      setIsCollapsed(false);
    } else {
      // If expanded, navigate to root (start new chat)
      onNewChat();
    }
  };

  return (
    <div className="relative flex items-center p-4 shrink-0 overflow-visible">
      {/* Wrap logo and name together in a single clickable container */}
      <button
        onClick={handleLogoClick}
        onMouseEnter={() => isCollapsed && setIsLogoHovered(true)}
        onMouseLeave={() => setIsLogoHovered(false)}
        className={cn(
          "flex items-center gap-2.5 min-w-0 flex-1 relative transition-all rounded-lg",
          isCollapsed
            ? "hover:bg-primary-50/50 cursor-pointer lg:cursor-e-resize p-0"
            : "hover:opacity-80 cursor-pointer -mx-2 px-2 py-1"
        )}
      >
        {/* Logo */}
        <div className="relative shrink-0">
          {isCollapsed && isLogoHovered ? (
            <div className="h-7 w-7 flex items-center justify-center">
              <PanelRight className="h-4 w-4 text-primary-600" />
            </div>
          ) : (
            <img
              src="/Logo_1.png"
              alt="Ivelion AI"
              className="h-7 w-7 aspect-square object-contain"
            />
          )}
          {/* Expand tooltip */}
          <AnimatePresence mode="wait">
            {isCollapsed && isLogoHovered && (
              <motion.div
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="absolute left-full ml-2 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-foreground/95 text-background px-2.5 py-1.5 rounded-md text-xs whitespace-nowrap pointer-events-none z-9999"
              >
                <PanelRight className="h-3.5 w-3.5" />
                <span>Open sidebar</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Company name - fades in/out */}
        <AnimatePresence>
          {!isCollapsed && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.15, delay: isCollapsed ? 0 : 0.05 }}
              className="text-base font-semibold text-foreground whitespace-nowrap"
            >
              Ivelion AI
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Collapse button - only visible when expanded */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute right-4 top-1/2 -translate-y-1/2"
          >
            <button
              ref={collapseButtonRef}
              onClick={() => {
                setIsCollapseHovered(false);
                setIsCollapsed(true);
              }}
              onMouseEnter={() => setIsCollapseHovered(true)}
              onMouseLeave={() => setIsCollapseHovered(false)}
              className="p-1.5 hover:bg-primary-50 rounded-lg transition-all hover:shadow-xs cursor-w-resize"
            >
              <PanelLeft className="h-4 w-4 text-primary-600" />
            </button>
            {/* Collapse tooltip - using Portal to escape overflow contexts */}
            <PortalTooltip
              isVisible={isCollapseHovered}
              targetRef={collapseButtonRef}
            >
              <PanelLeft className="h-3.5 w-3.5" />
              <span>Close sidebar</span>
            </PortalTooltip>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
